'use client';

import { createClient } from '@/lib/supabase/client';

export interface CollegeReview {
  id: string;
  collegeId: string;
  userId: string;
  overallRating: number;
  title: string;
  pros: string;
  cons: string;
  academics: number;
  faculty: number;
  infrastructure: number;
  placements: number;
  hostel: number;
  valueForMoney: number;
  batchYear?: number;
  course: string;
  isVerified: boolean;
  isPublished: boolean;
  helpfulCount: number;
  createdAt: string;
  // joined
  authorName?: string;
  authorEmail?: string;
}

export interface ReviewVote {
  id: string;
  reviewId: string;
  userId: string;
  isHelpful: boolean;
}

function mapRow(row: any): CollegeReview {
  return {
    id: row.id,
    collegeId: row.college_id,
    userId: row.user_id,
    overallRating: Number(row.overall_rating),
    title: row.title,
    pros: row.pros,
    cons: row.cons,
    academics: Number(row.academics),
    faculty: Number(row.faculty),
    infrastructure: Number(row.infrastructure),
    placements: Number(row.placements),
    hostel: Number(row.hostel),
    valueForMoney: Number(row.value_for_money),
    batchYear: row.batch_year,
    course: row.course,
    isVerified: row.is_verified,
    isPublished: row.is_published,
    helpfulCount: row.helpful_count,
    createdAt: row.created_at,
    authorName: row.user_profiles?.full_name,
    authorEmail: row.user_profiles?.email,
  };
}

export async function fetchCollegeReviews(collegeId: string): Promise<CollegeReview[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('college_reviews')
      .select('*, user_profiles(full_name, email)')
      .eq('college_id', collegeId)
      .eq('is_published', true)
      .order('helpful_count', { ascending: false });
    if (error) { console.error('fetchCollegeReviews error:', error.message); return []; }
    return (data || []).map(mapRow);
  } catch (err: any) {
    console.error('fetchCollegeReviews exception:', err.message);
    return [];
  }
}

export async function fetchAllReviewsForManager(collegeId: string): Promise<CollegeReview[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('college_reviews')
      .select('*, user_profiles(full_name, email)')
      .eq('college_id', collegeId)
      .order('created_at', { ascending: false });
    if (error) { console.error('fetchAllReviewsForManager error:', error.message); return []; }
    return (data || []).map(mapRow);
  } catch (err: any) {
    console.error('fetchAllReviewsForManager exception:', err.message);
    return [];
  }
}

export async function fetchUserReviewForCollege(
  userId: string,
  collegeId: string
): Promise<CollegeReview | null> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('college_reviews')
      .select('*')
      .eq('user_id', userId)
      .eq('college_id', collegeId)
      .single();
    if (error) return null;
    return mapRow(data);
  } catch {
    return null;
  }
}

export async function submitReview(
  review: Omit<CollegeReview, 'id' | 'isVerified' | 'isPublished' | 'helpfulCount' | 'createdAt' | 'authorName' | 'authorEmail'>
): Promise<CollegeReview | null> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('college_reviews')
      .upsert({
        college_id: review.collegeId,
        user_id: review.userId,
        overall_rating: review.overallRating,
        title: review.title,
        pros: review.pros,
        cons: review.cons,
        academics: review.academics,
        faculty: review.faculty,
        infrastructure: review.infrastructure,
        placements: review.placements,
        hostel: review.hostel,
        value_for_money: review.valueForMoney,
        batch_year: review.batchYear,
        course: review.course,
        is_published: false,
      }, { onConflict: 'college_id,user_id' })
      .select()
      .single();
    if (error) { console.error('submitReview error:', error.message); return null; }
    return mapRow(data);
  } catch (err: any) {
    console.error('submitReview exception:', err.message);
    return null;
  }
}

export async function updateReviewPublishStatus(
  reviewId: string,
  isPublished: boolean
): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase
      .from('college_reviews')
      .update({ is_published: isPublished })
      .eq('id', reviewId);
    if (error) { console.error('updateReviewPublishStatus error:', error.message); return false; }
    return true;
  } catch (err: any) {
    console.error('updateReviewPublishStatus exception:', err.message);
    return false;
  }
}

export async function voteReview(
  reviewId: string,
  userId: string,
  isHelpful: boolean
): Promise<boolean> {
  const supabase = createClient();
  try {
    const { error } = await supabase
      .from('review_votes')
      .upsert({ review_id: reviewId, user_id: userId, is_helpful: isHelpful }, { onConflict: 'review_id,user_id' });
    if (error) { console.error('voteReview error:', error.message); return false; }
    return true;
  } catch (err: any) {
    console.error('voteReview exception:', err.message);
    return false;
  }
}

export async function fetchUserVotes(userId: string, reviewIds: string[]): Promise<Record<string, boolean>> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('review_votes')
      .select('review_id, is_helpful')
      .eq('user_id', userId)
      .in('review_id', reviewIds);
    if (error) return {};
    const map: Record<string, boolean> = {};
    (data || []).forEach((v: any) => { map[v.review_id] = v.is_helpful; });
    return map;
  } catch {
    return {};
  }
}

export function subscribeToReviews(
  collegeId: string,
  onNew: (review: CollegeReview) => void
) {
  const supabase = createClient();
  const channel = supabase
    .channel(`reviews:${collegeId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'college_reviews',
        filter: `college_id=eq.${collegeId}`,
      },
      (payload) => {
        if (payload.new) onNew(mapRow(payload.new));
      }
    )
    .subscribe();
  return () => { supabase.removeChannel(channel); };
}
